import { Backdrop, Button, CircularProgress, Grid, Paper, TextField, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { lighten } from 'polished';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import AppStoreIcon from '../icons/AppStoreIcon';
import BaseLogoIcon from '../icons/BaseLogoIcon';
import GooglePlayIcon from '../icons/GooglePlayIcon';
import imageSrc from '../images/login.png';
import BaseNav from './nav/BaseNav';

/**
 * Extracts the first email address found in any JWT claim
 * @param {Object} token - The decoded JWT token object
 * @returns {string|undefined} - The extracted email or undefined if none found
 */
function extractEmailFromToken(token) {
  // Email regex pattern
  const emailRegex = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Common JWT claims that might contain email addresses
  const commonEmailClaims = ['email', 'upn', 'preferred_username', 'unique_name', 'mail'];

  // First check common claims as they're most likely to have the email
  for (const claim of commonEmailClaims) {
    if (token[claim] && typeof token[claim] === 'string' && emailRegex.test(token[claim])) {
      return token[claim];
    }
  }

  // If not found in common claims, check all claims
  for (const [key, value] of Object.entries(token)) {
    // Skip claims we already checked
    if (commonEmailClaims.includes(key)) continue;

    // Check if the claim value is a string and matches email pattern
    if (typeof value === 'string' && emailRegex.test(value)) {
      return value;
    }
  }

  return undefined;
}

export default function Login() {

  const theme = useTheme();
  const belowsm = useMediaQuery(theme.breakpoints.down("sm"));
  const belowmd = useMediaQuery(theme.breakpoints.down("md"));
  const belowlg = useMediaQuery(theme.breakpoints.down("lg"));
  const abovexl = useMediaQuery(theme.breakpoints.up("xl"));
  const below1000 = useMediaQuery('(max-width: 1000px)');

  const auth = useAuth();

  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);

  const { t } = useTranslation();
  document.title = t("vmm-login");

  const fromSession = useRef();

  useEffect(() => {
    document.body.style.background = theme.palette.base.main;
    document.body.style.backgroundImage = "none";
    //start the auth call
    if (localStorage.getItem("email")) {
      fromSession.current = localStorage.getItem("email");
      localStorage.removeItem("email");
      auth.signinRedirect({
        extraQueryParams: {
          email: fromSession.current,
          darkmode: JSON.parse(localStorage.getItem("darkMode"))
        }
      });
    }

    if (!hasAuthParams() &&
      !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading &&
      !hasTriedSignin
    ) {
      auth.signinSilent().then(res => {
        if (res?.id_token) {
          const token = jwtDecode(res.id_token);

          const email = extractEmailFromToken(token);

          if (email) {
            // fetchCustomer(email, true);
          } else {
            console.error("No email found in the token", token);
            // Handle the case when no email is found, maybe show a message to the user
          }
        }
      });
      setHasTriedSignin(true);
    };

    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    //eslint-disable-next-line
  }, [auth, hasTriedSignin]);

  const fetchCustomer = async (email, setEmail, setDisable, setError, setErrorMessage) => {
    await axios.get(localStorage.getItem("url") && (localStorage.getItem("url") !== undefined || localStorage.getItem("url") !== null) ? localStorage.getItem("url") + `/customer?email=${encodeURIComponent(email)}` : process.env.REACT_APP_apiUri + `/customer?email=${encodeURIComponent(email)}`).then(res => {
      if (res.data?.length > 0) {
        if (setEmail) {
          localStorage.setItem("email", email);
        }
        if (res.data[0].url === "https://connector-v2-api.mobilboxmanager.at/mbm-connector-generic") {
          localStorage.setItem("client", "connector");
        } else {
          localStorage.setItem("client", res.data[0].customerID === "mbm-connector-generic" ? "connector" : res.data[0].customerID);
        }
        localStorage.setItem("url", res.data[0].url);
        if (res.data[0].logo) {
          localStorage.setItem("logo", res.data[0].logo);
        }
        if (res.data[0].authority !== null && res.data[0].authority !== undefined) {
          localStorage.setItem("authority", res.data[0].authority);
        } else {
          localStorage.removeItem("authority");
        };
        if (res.data[0].externalClientId !== null && res.data[0].externalClientId !== undefined) {
          localStorage.setItem("externalClientId", res.data[0].externalClientId);
        } else {
          localStorage.removeItem("externalClientId");
        };
        if (res.data[0].externalScope !== null && res.data[0].externalScope !== undefined) {
          localStorage.setItem("externalScope", res.data[0].externalScope);
        } else {
          localStorage.removeItem("externalScope");
        };
        if (res.data[0].color !== null && res.data[0].color !== undefined) {
          localStorage.setItem("primary", res.data[0].color);
        } else {
          localStorage.removeItem("primary");
        };
        console.log(res.data);
        window.location.reload();
      } else {
        if (setDisable) {
          setDisable(false);

        }
        if (setError) {
          setError(true);
        }
        if (setErrorMessage) {
          setErrorMessage(t("email-does-not-exist"));
        }
      };
    }).catch(err => {
      console.error(err.response);
    });
  }

  const Form = ({ fromSession }) => {

    const [EmailString, setEmailString] = useState(fromSession || "");
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [disable, setDisable] = useState(fromSession ? true : false);

    const exec = () => {
      if (validate(EmailString)) {
        setError(false);
        setDisable(true)
        fetchCustomer(EmailString, true, setDisable, setError, setErrorMessage);
      } else {
        setError(true);
        setErrorMessage(t("invalid-email"));
      }
    }

    const enter = (event) => {
      if (event.keyCode === 13) {
        exec();
      }
    }

    const validate = (email) => {
      const regex = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return regex.test(email);
    };

    return (
      <Fragment>
        <Grid container sx={{ justifyContent: "center", alignContent: "center", pt: 4 }}>
          <BaseLogoIcon />
          <Typography sx={{ ml: 1.5, mt: 1.5, fontSize: "24px", fontStyle: "normal", fontWeight: 700, lineHeight: "normal" }}>Mobilbox Manager</Typography>
        </Grid>
        <TextField
          variant="outlined"
          label="E-Mail"
          type="email"
          value={EmailString}
          onChange={(event) => {
            if (event.target.value?.length === 0) {
              setError(false);
            }
            setEmailString(event.target.value);
          }}
          pattern="^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
          size="small"
          onKeyDown={enter}
          error={error}
          helperText={error ? errorMessage : " "}
          fullWidth
          required
          // InputProps={{
          //   endAdornment: (
          //     <InputAdornment
          //       position="end"
          //     >
          //       <IconButton
          //         onClick={exec}
          //         disabled={disable}
          //       >
          //         <ArrowCircleUpIcon
          //           sx={{ transform: "rotate(90deg)" }}
          //         />
          //       </IconButton>
          //     </InputAdornment>
          //   )
          // }}
          sx={{
            mt: 4,
            height: "62px",
            "& label": {
              mt: EmailString?.length > 0 ? 2 : 1,
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 700
            },
            "& label.Mui-focused": {
              mt: 2,
              color: "text.primary"
            },
            "& .MuiOutlinedInput-root": {
              pt: 2,
              pb: .5,
              borderRadius: "8px",
              bgcolor: theme.palette.mode === "dark" ? "#000" : "#fff"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none"
            }
          }}
          disabled={disable}
        />
        <Button
          variant="contained"
          color="base"
          disableElevation
          fullWidth
          onClick={exec}
          disabled={disable}
          sx={{
            mt: error ? 3.5 : 1.5,
            mb: error ? 2 : 4,
            height: "62px",
            borderRadius: "8px",
            textTransform: "none"
          }}
        >
          <Typography sx={{ fontSize: "16px", fontStyle: "normal", textAlign: "center", fontWeight: 700, lineHeight: "normal", color: '#fff' }}>{t("login")}</Typography>
        </Button>
      </Fragment>
    );
  }

  if (auth.isLoading) {
    return (
      <Backdrop
        sx={{ zIndex: theme.zIndex.drawer + 1 }}
        invisible
        open={true}
      >
        <CircularProgress
          size={108}
          thickness={2}
          sx={{ color: "#fff" }}
        />
      </Backdrop>
    )
  }

  if (auth.isAuthenticated === false) {
    return (
      <Fragment>
        <Box sx={{ mt: below1000 ? 0 : 3, mx: 6 }}>
          <BaseNav />
        </Box>
        <Grid container>
          <Grid item xs={6} sx={{ display: belowmd ? "none" : "flex", justifyContent: "flex-end", alignContent: "center", pr: 8, mt: abovexl ? 15 : 0 }}>
            <img alt="login" src={imageSrc} width="70%" style={{ aspectRatio: 1000 / 1296, objectFit: "scale-down" }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: height,
                display: 'flex',
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  mt: belowlg && !belowsm ? 10 : 0,
                  px: 4,
                  backgroundImage: "none",
                  bgcolor: theme.palette.mode === "dark" ? lighten(0.05, theme.palette.background.paper) : "base.secondary",
                  borderRadius: 6,
                  display: 'inherit',
                  flexDirection: "inherit",
                  justifyContent: "inherit",
                  alignItems: "inherit"
                }}
              >
                <Form fromSession={fromSession.current} />
              </Paper>
              <Grid container columnGap={1.5} sx={{ justifyContent: "center", mt: -3.5, display: { xs: "none", md: "flex" } }}>
                <GooglePlayIcon />
                <Grid item sx={{ mt: 1.2 }}>
                  <AppStoreIcon />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  return <Navigate replace to="/dashboard?fromlogin=true" />;
};